import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './HomeOwnerNavbar.module.css';
import { sessionService, homeownerBillingService, userService, proposalComparisonService, backgroundProcessService } from '../../../services/api';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import PlanUsageModal from '../../common/PlanUsageModal/PlanUsageModal';
import BackgroundProcessModal from '../../common/BackgroundProcessModal/BackgroundProcessModal';

const HomeOwnerNavbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isPlanTooltipVisible, setIsPlanTooltipVisible] = useState(false);
  const [tooltipUsageData, setTooltipUsageData] = useState(null);
  const [modalRefreshTrigger, setModalRefreshTrigger] = useState(0);
  const [isBackgroundProcessModalOpen, setIsBackgroundProcessModalOpen] = useState(false);
  const [backgroundProcessCount, setBackgroundProcessCount] = useState({ running: 0, total: 0 });
  const [backgroundProcessLoading, setBackgroundProcessLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Add isLoggedIn check
  const isLoggedIn = localStorage.getItem('token') !== null;
  const user = isLoggedIn ? JSON.parse(localStorage.getItem('user')) : null;
  
  // Get subscription status
  const { subscriptionData, hasActiveSubscription, isLoading: subscriptionLoading, refreshSubscriptionStatus } = useSubscriptionStatus();
  
  // Temporary direct API call for debugging
  const [directSubscriptionData, setDirectSubscriptionData] = useState(null);
  
    const fetchDirectSubscription = async () => {
      if (isLoggedIn) {
        try {
          const response = await homeownerBillingService.getSubscription();
          console.log('Navbar - Direct API call result:', response);
          setDirectSubscriptionData(response.data.data);
        
        // Clear stale tooltip data immediately when subscription changes
        setTooltipUsageData(null);
        
        // Refresh tooltip data when subscription data changes
        setTimeout(() => {
          fetchTooltipUsageData();
        }, 500);
        } catch (error) {
          console.error('Navbar - Direct API call error:', error);
        }
      }
    };
    
  useEffect(() => {
    fetchDirectSubscription();
    fetchBackgroundProcessCount();
  }, [isLoggedIn]);

  // Add event listener for plan usage refresh
  useEffect(() => {
    const handlePlanUsageRefresh = () => {
      console.log('Navbar - Received plan usage refresh event');
      // Clear stale tooltip data immediately
      setTooltipUsageData(null);
      // Refresh subscription data
      refreshSubscriptionStatus();
      // Refresh direct subscription data
        fetchDirectSubscription();
      // Refresh tooltip data with a small delay
      setTimeout(() => {
        fetchTooltipUsageData();
      }, 500);
    };

    // Listen for custom refresh event
    window.addEventListener('refreshPlanUsage', handlePlanUsageRefresh);
    
    return () => {
      window.removeEventListener('refreshPlanUsage', handlePlanUsageRefresh);
    };
  }, [isLoggedIn, refreshSubscriptionStatus]);

  const handleLogout = async () => {
    try {
      // Call our session service to properly end the session on the server
      await sessionService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with logout even if server request fails
    } finally {
      // Always clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/homeowner');
      window.location.reload();
    }
  };

  const handleDropdownClick = (dropdownName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleSubDropdownClick = (subDropdownName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveSubDropdown(activeSubDropdown === subDropdownName ? null : subDropdownName);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveDropdown(null);
      setIsUserDropdownOpen(false);
    }
  };

  const isActivePath = (path) => {
    if (path === '/homeowner') {
      return location.pathname === '/homeowner';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavLinkClick = () => {
    setActiveDropdown(null);
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleUserDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setActiveDropdown(null);
  };

  const handlePlanIndicatorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPlanModalOpen(true);
    setIsUserDropdownOpen(false);
    setActiveDropdown(null);
    // Only trigger modal refresh - modal will fetch its own data
    setModalRefreshTrigger(prev => prev + 1);
  };

  const fetchBackgroundProcessCount = async () => {
    if (!isLoggedIn) return;
    
    try {
      setBackgroundProcessLoading(true);
      const response = await backgroundProcessService.getProcessCount();
      if (response.data && response.data.status === 'success') {
        setBackgroundProcessCount(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching background process count:', error);
    } finally {
      setBackgroundProcessLoading(false);
    }
  };

  const handleBackgroundProcessClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBackgroundProcessModalOpen(true);
    setIsUserDropdownOpen(false);
    setActiveDropdown(null);
  };

  const fetchTooltipUsageData = async () => {
    try {
      console.log('Navbar - Fetching tooltip usage data...');
      const currentPlanName = getPlanDisplayName(subscriptionData, directSubscriptionData);
      const isFreeTier = currentPlanName === 'Free';
      
      // Use the SAME logic as PlanUsageModal - get permanent counter from getUserStats
      const [userStatsResponse, addonComparisonsResponse] = await Promise.all([
        userService.getUserStats(), // This includes the permanent comparisons counter
        // ALWAYS fetch addon comparisons for all users (not just non-free) to ensure fresh data
        homeownerBillingService.getUserAddonComparisons().catch(() => ({ data: { data: { addonComparisons: 0 } } }))
      ]);

      const userStats = userStatsResponse.data?.data || {};
      const comparisonsCount = userStats.proposalComparisons || 0; // Use permanent counter from getUserStats
      const addonComparisons = Number(addonComparisonsResponse.data?.data?.addonComparisons) || 0;
      
      // console.log('Navbar - User stats:', userStats);
      // console.log('Navbar - Permanent comparisons count:', comparisonsCount);
      // console.log('Navbar - Addon comparisons:', addonComparisons);
      // console.log('Navbar - Current plan:', currentPlanName);
      
      const newTooltipData = {
        filesUploaded: userStats.filesUploaded || 0,
        projectComparisons: comparisonsCount,
        storageUsed: userStats.storageUsedMB || 0,
        addonComparisons: addonComparisons
      };
      
      // console.log('Navbar - Setting tooltip data:', newTooltipData);
      setTooltipUsageData(newTooltipData);
    } catch (error) {
      console.error('Error fetching tooltip usage data:', error);
      setTooltipUsageData({
        filesUploaded: 0,
        projectComparisons: 0,
        storageUsed: 0,
        addonComparisons: 0
      });
    }
  };

  const handlePlanIndicatorMouseEnter = () => {
    setIsPlanTooltipVisible(true);
    // Always fetch fresh data on hover to ensure accuracy
      fetchTooltipUsageData();
  };

  const handlePlanIndicatorMouseLeave = () => {
    setIsPlanTooltipVisible(false);
  };

  const getStorageLimits = (planName) => {
    const plan = planName?.toLowerCase();
    switch (plan) {
      case 'complete':
        return 1024; // 1GB
      case 'expert':
        return 5120; // 5GB
      case 'essential':
        return 250; // 250MB
      case 'free':
      default:
        return 250; // 250MB
    }
  };

  const getComparisonLimits = (planName, addonComparisons = 0) => {
    const plan = planName?.toLowerCase();
    let baseLimit;
    switch (plan) {
      case 'essential':
        baseLimit = 5; // 5 comparisons
        break;
      case 'complete':
        baseLimit = 10; // 10 comparisons
        break;
      case 'expert':
        baseLimit = 20; // 20 comparisons
        break;
      case 'free':
      default:
        baseLimit = 2; // 2 comparisons for free tier
        break;
    }
    
    // Convert both to numbers to prevent string concatenation
    const numericBaseLimit = Number(baseLimit);
    const numericAddonComparisons = Number(addonComparisons) || 0;
    
    return numericBaseLimit + numericAddonComparisons;
  };

  const formatStorage = (sizeInMB) => {
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeInMB} MB`;
  };

  const getPlanDisplayName = (subscriptionData, directData) => {
    // Debug logging to see what we're receiving
    console.log('Navbar - subscriptionData (hook):', subscriptionData);
    console.log('Navbar - directSubscriptionData (direct API):', directData);
    console.log('Navbar - hasActiveSubscription:', hasActiveSubscription);
    console.log('Navbar - subscriptionLoading:', subscriptionLoading);
    
    // Try direct API data first, then hook data
    const dataToUse = directData || subscriptionData;
    
    // If we have subscription data with a plan name, show it regardless of active status
    if (dataToUse && dataToUse.plan_name) {
      const planName = dataToUse.plan_name.toLowerCase();
      switch (planName) {
        case 'essential':
          return 'Essential';
        case 'complete':
          return 'Complete';
        case 'expert':
          return 'Expert';
        default:
          return dataToUse.plan_name;
      }
    }
    
    // If no subscription data, show Free
    return 'Free';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close all dropdowns when opening/closing the mobile menu
    setActiveDropdown(null);
    setActiveSubDropdown(null);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add body lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add(styles.bodyLock);
    } else {
      document.body.classList.remove(styles.bodyLock);
    }
  }, [isMobileMenuOpen]);

  // Listen for subscription updates
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      console.log('Navbar: Subscription updated, refreshing data');
      // Refresh subscription data immediately
      fetchDirectSubscription();
      // Force refresh the tooltip data immediately with a small delay to ensure backend sync
      setTooltipUsageData(null);
      setTimeout(() => {
        fetchTooltipUsageData();
      }, 300); // Small delay to ensure backend processing is complete
    };

    // Check for localStorage flags immediately on load and periodically
    const checkForUpdates = () => {
      const subscriptionCompleted = localStorage.getItem('subscriptionCompleted');
      const addonPurchaseCompleted = localStorage.getItem('addonPurchaseCompleted');
      
      if (subscriptionCompleted === 'true') {
        // console.log('Navbar: Detected subscription completion, triggering immediate refresh');
        localStorage.removeItem('subscriptionCompleted');
        localStorage.removeItem('subscriptionTimestamp');
        handleSubscriptionUpdate();
      }
      
      if (addonPurchaseCompleted === 'true') {
        // console.log('Navbar: Detected addon purchase completion, triggering immediate refresh');
        localStorage.removeItem('addonPurchaseCompleted');
        localStorage.removeItem('addonPurchaseTimestamp');
        handleSubscriptionUpdate();
      }
    };

    // Check immediately
    checkForUpdates();
    
    // Set up periodic checking (every 2 seconds for 30 seconds after component loads)
    const interval = setInterval(checkForUpdates, 2000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 30000); // Stop checking after 30 seconds

    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Poll background process count every 30 seconds
  useEffect(() => {
    if (!isLoggedIn) return;

    const interval = setInterval(() => {
      fetchBackgroundProcessCount();
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  return (
    <nav className={styles.navbar} ref={dropdownRef}>
      <div className={styles.navbarContainer}>
        <div className={styles.navbarBrand}>
          <Link to="/" onClick={() => handleDropdownClick()}>
            <span className={styles.brandText}>
              {/* <span className={styles.brandTextSmart}>Smart</span>
              <span className={styles.brandTextTech}>Tech</span>
              {' '}
              <span className={styles.brandTextPro}>ProServe</span> */}
              <span className={styles.brandTextTrakvu}>Trakvu</span>
            </span>
          </Link>
          {/* <div className={styles.homeOwnerBadge}>Home Owner</div> */}
        </div>

        {/* Hamburger button for mobile */}
        <button 
          className={`${styles.hamburger} ${isMobileMenuOpen ? styles.active : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Desktop Navigation */}
        <div className={styles.navbarMenu}>
          <div className={styles.navbarStart}>
          {isLoggedIn && (
          <>
          <Link to="/homeowner/dashboard" className={styles.navItem} onClick={handleNavLinkClick}>
              Dashboard
            </Link>
            </>
            )}  
            <div className={styles.navItem}>
              <div className={`${styles.navItemDropdown} ${activeDropdown === 'solutions' ? styles.active : ''}`}>
                <span onClick={(e) => handleDropdownClick('solutions', e)}>Solutions</span>
                <div className={styles.megaDropdown}>
                  {/* Main Sections */}
                  <div className={styles.mainSections}>
                    {/* Proposal Analysis */}
                    <div 
                      className={`${styles.mainSection} ${activeDropdown === 'proposal' ? styles.active : ''}`}
                      onClick={(event) => handleDropdownClick('proposal', event)}
                    >
                      <h3>
                        <i className="fas fa-robot"></i>
                        Proposal Analysis
                      </h3>
                      <div className={styles.subCategories}>
                        <Link to="/upload-compare-proposals" onClick={handleNavLinkClick}>
                          <i className="fas fa-upload"></i>
                          Upload & Compare Proposals
                        </Link>
                        <Link to="/ai-proposal-analysis" onClick={handleNavLinkClick}>
                          <i className="fas fa-brain"></i>
                          AI Proposal Analysis
                        </Link>
                        <Link to="/contractor-ratings" onClick={handleNavLinkClick}>
                          <i className="fas fa-star"></i>
                          Contractor Ratings
                        </Link>
                        <Link to="/clarification-questions" onClick={handleNavLinkClick}>
                          <i className="fas fa-question-circle"></i>
                          Clarification Questions
                        </Link>
                      </div>
                    </div>



                    {/* Resources */}
                    <div 
                      className={`${styles.mainSection} ${activeDropdown === 'resources' ? styles.active : ''}`}
                      onClick={(event) => handleDropdownClick('resources', event)}
                    >
                      <h3>
                        <i className="fas fa-book"></i>
                        Resources
                      </h3>
                      <div className={styles.subCategories}>
                        <Link to="/learn" onClick={handleNavLinkClick}>
                          <i className="fas fa-graduation-cap"></i>
                          Learning Center
                        </Link>
                        <Link to="/upgrade" onClick={handleNavLinkClick}>
                          <i className="fas fa-gem"></i>
                          Premium Features
                        </Link>
                        <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                          <i className="fas fa-life-ring"></i>
                          Support & Help
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/homeowner/pricing" className={styles.navItem} onClick={handleNavLinkClick}>
              Pricing
            </Link>
            <div className={styles.navItem}>
              <div className={`${styles.navItemDropdown} ${activeDropdown === 'support' ? styles.active : ''}`}>
                <span onClick={(e) => handleDropdownClick('support', e)}>Support</span>
                <div className={styles.dropdown}>
                  <div className={styles.supportColumns}>
                    <div className={styles.supportColumn}>
                      <h3 className={styles.supportColumnTitle}>
                        <i className="fas fa-users"></i> Community Support
                      </h3>
                      <Link to="/homeowner/knowledge-base" onClick={handleNavLinkClick}>
                        <i className="fas fa-book"></i>
                        Knowledge Base
                      </Link>
                      <Link to="/homeowner/tutorials" onClick={handleNavLinkClick}>
                        <i className="fas fa-graduation-cap"></i>
                        Tutorials
                      </Link>
                    </div>
                    
                    <div className={styles.supportColumn}>
                      <h3 className={styles.supportColumnTitle}>
                        <i className="fas fa-envelope"></i> Contact Us
                      </h3>
                      <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                        <i className="fas fa-paper-plane"></i>
                        Send Message
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.active : ''}`}>
          <div className={styles.mobileNav}>
            {/* Solutions Dropdown - Mobile */}
            {isLoggedIn && (
            <>
            <Link to="/homeowner/dashboard" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
              Dashboard
            </Link>
            </>
            )}
            <div className={`${styles.mobileNavItem} ${activeDropdown === 'solutions' ? styles.active : ''}`}>
              <div 
                className={styles.mobileNavHeader} 
                onClick={(e) => handleDropdownClick('solutions', e)}
              >
                <span>Solutions</span>
                <i className="fas fa-chevron-down"></i>
              </div>
              <div className={styles.mobileNavContent}>
                {/* Proposal Analysis - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'proposal' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('proposal', e)}
                  >
                    <i className="fas fa-robot"></i>
                    <span>Proposal Analysis</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/upload-compare-proposals" onClick={handleNavLinkClick}>
                      <i className="fas fa-upload"></i>
                      Upload & Compare Proposals
                    </Link>
                    <Link to="/ai-proposal-analysis" onClick={handleNavLinkClick}>
                      <i className="fas fa-brain"></i>
                      AI Proposal Analysis
                    </Link>
                    <Link to="/contractor-ratings" onClick={handleNavLinkClick}>
                      <i className="fas fa-star"></i>
                      Contractor Ratings
                    </Link>
                    <Link to="/clarification-questions" onClick={handleNavLinkClick}>
                      <i className="fas fa-question-circle"></i>
                      Clarification Questions
                    </Link>
                  </div>
                </div>



                {/* Resources - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'resources' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('resources', e)}
                  >
                    <i className="fas fa-book"></i>
                    <span>Resources</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/learn" onClick={handleNavLinkClick}>
                      <i className="fas fa-graduation-cap"></i>
                      Learning Center
                    </Link>
                    <Link to="/upgrade" onClick={handleNavLinkClick}>
                      <i className="fas fa-gem"></i>
                      Premium Features
                    </Link>
                    <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                      <i className="fas fa-life-ring"></i>
                      Support & Help
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Regular Nav Items - Mobile */}
            <Link to="/homeowner/pricing" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
              Pricing
            </Link>
            

            
            {/* Support Dropdown - Mobile */}
            <div className={`${styles.mobileNavItem} ${activeDropdown === 'mobileSupport' ? styles.active : ''}`}>
              <div 
                className={styles.mobileNavHeader} 
                onClick={(e) => handleDropdownClick('mobileSupport', e)}
              >
                <span>Support</span>
                <i className="fas fa-chevron-down"></i>
              </div>
              <div className={styles.mobileNavContent}>
                {/* Community Support - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'communitySupport' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('communitySupport', e)}
                  >
                    <i className="fas fa-users"></i>
                    <span>Community Support</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/homeowner/knowledge-base" onClick={handleNavLinkClick}>
                      <i className="fas fa-book"></i>
                      Knowledge Base
                    </Link>
                    <Link to="/homeowner/tutorials" onClick={handleNavLinkClick}>
                      <i className="fas fa-graduation-cap"></i>
                      Tutorials
                    </Link>
                  </div>
                </div>

                {/* Contact Us - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'contactUs' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('contactUs', e)}
                  >
                    <i className="fas fa-envelope"></i>
                    <span>Contact Us</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                      <i className="fas fa-paper-plane"></i>
                      Send Message
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Login/Profile Section - Mobile */}
            {isLoggedIn ? (
              <div className={styles.mobileUserInfo}>
                <div className={styles.userAvatar}>
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
                <div>
                  <div className={styles.userName}>{user?.firstName} {user?.lastName}</div>
                  <div className={styles.userEmail}>{user?.email}</div>
                </div>
              </div>
            ) : (
              <div className={styles.mobileLoginContainer}>
                <Link to="/homeowner/login" className={styles.mobileLoginButton} onClick={handleNavLinkClick}>
                  Login
                </Link>
              </div>
            )}
            
            {/* User Profile Links - Mobile */}
            {isLoggedIn && (
              <>
                <Link to="/homeowner/profile" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-user"></i>
                  My Profile
                </Link>
                <Link to="/homeowner/billing" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-credit-card"></i>
                  Billing
                </Link>
                <Link to="/homeowner/settings" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-cog"></i>
                  Settings
                </Link>
                <button 
                  onClick={handleLogout} 
                  className={styles.mobileLogoutButton}
                >
                  <i className="fas fa-sign-out-alt"></i>
                  Logout
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.desktopButtons}>
          
          {isLoggedIn ? (
            <div className={styles.userSection}>
              {/* Background Process Indicator */}
              <div className={styles.backgroundProcessWrapper}>
                <button 
                  className={styles.backgroundProcessIndicator}
                  onClick={handleBackgroundProcessClick}
                  disabled={backgroundProcessLoading}
                  title="Click to view background processes"
                >
                  <i className="fas fa-cogs"></i>
                  {backgroundProcessCount.running > 0 && (
                    <span className={styles.processBadge}>
                      {backgroundProcessCount.running}
                    </span>
                  )}
                </button>
              </div>

              {/* Plan Indicator */}
              <div className={styles.planIndicatorWrapper}>
                <button 
                  className={styles.planIndicator}
                  onClick={handlePlanIndicatorClick}
                  onMouseEnter={handlePlanIndicatorMouseEnter}
                  onMouseLeave={handlePlanIndicatorMouseLeave}
                  disabled={subscriptionLoading}
                  title="Click to view detailed usage. Each create/edit/delete action costs 1 comparison."
                >
                  <i className="fas fa-crown"></i>
                  <span>
                    {subscriptionLoading ? 'Loading...' : getPlanDisplayName(subscriptionData, directSubscriptionData)}
                  </span>
                </button>
                
                {/* Plan Usage Tooltip */}
                {isPlanTooltipVisible && tooltipUsageData && (
                  <div className={styles.planTooltip}>
                    <div className={styles.tooltipContent}>
                      <div className={styles.tooltipItem}>
                        <i className="fas fa-hdd"></i>
                        <span>Storage: {formatStorage(tooltipUsageData.storageUsed)} / {formatStorage(getStorageLimits(getPlanDisplayName(subscriptionData, directSubscriptionData)))}</span>
                      </div>
                      <div className={styles.tooltipItem}>
                        <i className="fas fa-file-upload"></i>
                        <span>Files uploaded: {tooltipUsageData.filesUploaded}</span>
                      </div>
                      <div className={styles.tooltipItem}>
                        <i className="fas fa-chart-bar"></i>
                        <span>Comparisons: {(() => {
                          const currentPlan = getPlanDisplayName(subscriptionData, directSubscriptionData);
                          const addonComparisons = tooltipUsageData.addonComparisons || 0;
                          const limit = getComparisonLimits(currentPlan, addonComparisons);
                          return limit === null 
                            ? `${tooltipUsageData.projectComparisons} / Unlimited`
                            : `${tooltipUsageData.projectComparisons}/${limit}`;
                        })()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            <div className={styles.userDropdown}>
              <button 
                className={styles.userButton}
                onClick={handleUserDropdownClick}
              >
                <div className={styles.userAvatar}>
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              </button>

              {isUserDropdownOpen && (
                <div 
                  className={styles.dropdown}
                  style={{ display: 'block' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.userInfo}>
                    <div className={styles.userName}>
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className={styles.userEmail}>{user?.email}</div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <Link 
                    to="/homeowner/profile" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-user"></i>
                    My Profile
                  </Link>
                  <Link 
                    to="/homeowner/billing" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-credit-card"></i>
                    Billing
                  </Link>
                  <Link 
                    to="/homeowner/settings" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-cog"></i>
                    Settings
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsUserDropdownOpen(false);
                      handleLogout();
                    }} 
                    className={styles.dropdownItem}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Logout
                  </button>
                </div>
              )}
              </div>
            </div>
          ) : (
            <Link to="/homeowner/login" className={styles.loginButton}>
              Login
            </Link>
          )}
        </div>
      </div>

      {/* Plan Usage Modal */}
      <PlanUsageModal 
        isOpen={isPlanModalOpen} 
        onClose={() => setIsPlanModalOpen(false)} 
        subscriptionData={directSubscriptionData || subscriptionData}
        forceRefresh={modalRefreshTrigger}
      />

      {/* Background Process Modal */}
      <BackgroundProcessModal 
        isOpen={isBackgroundProcessModalOpen} 
        onClose={() => setIsBackgroundProcessModalOpen(false)} 
      />
    </nav>
  );
};

export default HomeOwnerNavbar; 